import { useState, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';

const uid = () => Math.random().toString(36).slice(2, 8);

export function usePdfOrganizer() {
  const [pages, setPages] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const initPages = useCallback((pageCount) => {
    setPages(
      Array.from({ length: pageCount }, (_, i) => ({
        id: `page-${i}-${uid()}`,
        originalIndex: i,
      })),
    );
    setSelectedIds(new Set());
  }, []);

  const reorderPages = useCallback((activeId, overId) => {
    setPages((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === activeId);
      const newIndex = prev.findIndex((p) => p.id === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const deletePage = useCallback((id) => {
    setPages((prev) => prev.filter((p) => p.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const deleteSelected = useCallback(() => {
    setPages((prev) => prev.filter((p) => !selectedIds.has(p.id)));
    setSelectedIds(new Set());
  }, [selectedIds]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setPages((prev) => {
      setSelectedIds(new Set(prev.map((p) => p.id)));
      return prev;
    });
  }, []);

  const deselectAll = useCallback(() => setSelectedIds(new Set()), []);

  // ── Move operations (work on the current selection) ──────────────────────────

  const moveUp = useCallback(() => {
    setPages((prev) => {
      const positions = prev
        .map((p, i) => (selectedIds.has(p.id) ? i : -1))
        .filter((i) => i >= 0)
        .sort((a, b) => a - b);
      if (positions.length === 0 || positions[0] === 0) return prev;
      const next = [...prev];
      for (const idx of positions) {
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      }
      return next;
    });
  }, [selectedIds]);

  const moveDown = useCallback(() => {
    setPages((prev) => {
      const positions = prev
        .map((p, i) => (selectedIds.has(p.id) ? i : -1))
        .filter((i) => i >= 0)
        .sort((a, b) => b - a); // descending so we swap from the bottom up
      if (positions.length === 0 || positions[0] === prev.length - 1) return prev;
      const next = [...prev];
      for (const idx of positions) {
        if (idx < next.length - 1) {
          [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        }
      }
      return next;
    });
  }, [selectedIds]);

  const moveToFirst = useCallback(() => {
    setPages((prev) => {
      const selected = prev.filter((p) => selectedIds.has(p.id));
      const rest = prev.filter((p) => !selectedIds.has(p.id));
      return [...selected, ...rest];
    });
  }, [selectedIds]);

  const moveToLast = useCallback(() => {
    setPages((prev) => {
      const selected = prev.filter((p) => selectedIds.has(p.id));
      const rest = prev.filter((p) => !selectedIds.has(p.id));
      return [...rest, ...selected];
    });
  }, [selectedIds]);

  const reset = useCallback(() => {
    setPages([]);
    setSelectedIds(new Set());
  }, []);

  return {
    pages,
    selectedIds,
    initPages,
    reorderPages,
    deletePage,
    deleteSelected,
    toggleSelect,
    selectAll,
    deselectAll,
    moveUp,
    moveDown,
    moveToFirst,
    moveToLast,
    reset,
  };
}
